import type { ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'
import { cn } from 'cn'
import { createRoot } from 'react-dom/client'
import { Mention } from 'shared/components/editor/mention'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useHighlightSearchContext } from 'modules/community/context/highlight-search'
import { getTextAroundWord } from 'modules/community/utils/get-text-around-word'
import { parseTextWithMentions } from './parse-text-with-mentions'

interface TextWithMentionsProps extends React.HTMLProps<HTMLDivElement> {
  text: string
  mentions: MentionInterface[]
  truncateLength?: number
  onTruncationChange?: (isTruncated: boolean) => void
}

export const TextWithMentions = ({
  text,
  mentions,
  truncateLength,
  onTruncationChange,
  ...rest
}: TextWithMentionsProps): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null)
  const highlightContext = useHighlightSearchContext()

  useEffect(() => {
    if (containerRef.current) {
      let finalHtml = parseTextWithMentions(text, mentions)
      let isTextTruncated = false
      if (truncateLength) {
        const measure = document.createElement('div')
        measure.innerHTML = finalHtml
        const textLen = measure.textContent?.length ?? 0
        isTextTruncated = textLen > truncateLength
        if (highlightContext?.highlightKeyword) {
          finalHtml = highlightContext.wrapWithHighlight(finalHtml, truncateLength)
        } else if (isTextTruncated) {
          finalHtml = getTextAroundWord(finalHtml, truncateLength)
        }
      }

      containerRef.current.innerHTML = finalHtml

      const updateTruncation = (): void => {
        const container = containerRef.current
        if (!container) return
        onTruncationChange?.(
          isTextTruncated || (!!truncateLength && container.scrollHeight > container.clientHeight),
        )
      }
      updateTruncation()

      let resizeObserver: ResizeObserver | undefined
      if (truncateLength && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateTruncation)
        resizeObserver.observe(containerRef.current)
      }

      const mentionElements =
        containerRef.current.querySelectorAll<HTMLSpanElement>('[data-type="mention"]')
      if (mentionElements)
        for (const element of mentionElements) {
          const { id } = element.dataset
          const displayName = element.dataset.cy?.split('mention-').pop()
          const profileImageUrl = element.dataset.avatarUrl

          const root = createRoot(element)
          root.render(
            <Mention
              element={{
                displayName: displayName || '',
                id: Number(id) || 0,
                profileImageUrl: profileImageUrl || '',
              }}
            />,
          )
        }

      return () => resizeObserver?.disconnect()
    }
  }, [
    text,
    mentions,
    truncateLength,
    onTruncationChange,
    highlightContext?.highlightKeyword,
    highlightContext?.wrapWithHighlight,
  ])

  return (
    <div
      ref={containerRef}
      {...rest}
      dir="auto"
      className={cn(
        'text-start [&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:list-disc [&_ul]:ps-5',
        rest.className,
      )}
    />
  )
}
